/**
 * Place auction bid — what a bidder posts to `POST /api/auctions/place-bid`
 * to increment the high bid on a live auction. The route delegates the
 * "amount > current high bid + min increment" check + extension logic to the
 * `placeBid` domain function (DB-state-dependent); the boundary parse is
 * shape + sanity bound only. Web-only (mobile never hits it; auction-bid UI
 * lives only in the web app).
 *
 * Bounds mirror the route's prior inline check (0 < amount <= 999_999) so the
 * schema is a 1:1 lift of the previous defensive sanity bound. The actual
 * minimum required bid is still computed inside `placeBid` against the live
 * highest-bid row — the schema only catches obviously-broken payloads (NaN,
 * negative, missing fields, runaway integers) before the DB roundtrip.
 *
 * @see app/api/auctions/place-bid/route.ts — handler
 * @see lib/auction-bids.ts placeBid — semantic invariants (min-increment, CAS)
 * @see components/detailed-listing-view/auction-panel.tsx — caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md CRITICAL-4c
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Defensive sanity cap on bid amount. Mirrors the pre-schema inline route
 * check (`amount > 999_999`). Real auctions are far below this; the cap is
 * just to bound runaway-integer / overflow scenarios. The live minimum
 * required bid is computed inside `placeBid`.
 */
export const BID_AMOUNT_MAX = 999_999;

export interface PlaceAuctionBidRequest {
  /** Target `listings.id` of the live auction. */
  listing_id: string;
  /** Bid amount in listing currency. 0 < amount <= 999_999. */
  amount: number;
  /**
   * Optional client-supplied idempotency token. Stored on the bid row so a
   * web-side retry of the same logical action coalesces server-side.
   */
  client_bid_id?: string;
}

export const PlaceAuctionBidRequestSchema = pinned<PlaceAuctionBidRequest>()(
  z.object({
    listing_id: z.string().min(1),
    amount: z.number().finite().positive().max(BID_AMOUNT_MAX),
    client_bid_id: z.string().min(1).optional(),
  }),
);
